import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { useDashboardStore } from '../stores/dashboardStore';
import './Resume.css';

// Setup PDF.js worker
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Setup PDF.js worker
import * as pdfjs from 'pdfjs-dist';
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const Resume: React.FC = () => {
    const { config } = useDashboardStore();
    const [selectedLanguage, setSelectedLanguage] = useState<'chinese' | 'english'>('chinese');
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleLanguageChange = (language: 'chinese' | 'english') => {
        setSelectedLanguage(language);
        setPageNumber(1);
    };

    const handleZoomIn = () => {
        setScale(prev => Math.min(prev + 0.2, 2.0));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(prev - 0.2, 0.5));
    };

    const handleResetZoom = () => {
        setScale(1.0);
    };

    const goToPrevPage = () => {
        setPageNumber(prev => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        setPageNumber(prev => Math.min(prev + 1, numPages || 1));
    };

    const downloadResume = () => {
        // Check if file exists
        const filePath = selectedLanguage === 'chinese' ? config.resume.chinese : config.resume.english;
        const link = document.createElement('a');
        link.href = filePath;
        link.download = `resume-${selectedLanguage}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="resume-page">
            <div className="container">
                <div className="resume-header">
                    <h1>Resume</h1>
                    <p>Click the download button to download the PDF version of the resume</p>
                </div>

                <div className="resume-controls">
                    <div className="language-selector">
                        <label>Select Language:</label>
                        <div className="language-buttons">
                            <button
                                className={`lang-btn ${selectedLanguage === 'chinese' ? 'active' : ''}`}
                                onClick={() => handleLanguageChange('chinese')}
                            >
                                Chinese Resume
                            </button>
                            <button
                                className={`lang-btn ${selectedLanguage === 'english' ? 'active' : ''}`}
                                onClick={() => handleLanguageChange('english')}
                            >
                                English Resume
                            </button>
                        </div>
                    </div>

                    <div className="pdf-controls">
                        <div className="zoom-controls">
                            <button onClick={handleZoomOut} className="control-btn" title="Zoom Out">
                                −
                            </button>
                            <span className="zoom-level">{Math.round(scale * 100)}%</span>
                            <button onClick={handleZoomIn} className="control-btn" title="Zoom In">
                                +
                            </button>
                            <button onClick={handleResetZoom} className="control-btn" title="Reset">
                                ↺
                            </button>
                        </div>

                        <div className="page-controls">
                            <button
                                onClick={goToPrevPage}
                                disabled={pageNumber <= 1}
                                className="control-btn"
                                title="Previous Page"
                            >
                                ‹
                            </button>
                            <span className="page-info">
                                Page {pageNumber} of {numPages || 0}
                            </span>
                            <button
                                onClick={goToNextPage}
                                disabled={pageNumber >= (numPages || 1)}
                                className="control-btn"
                                title="Next Page"
                            >
                                ›
                            </button>
                        </div>

                        <button onClick={downloadResume} className="download-btn">
                            📥 Download Resume
                        </button>
                    </div>
                </div>

                <div className="pdf-container">
                    <div className="pdf-viewer">
                        <Document
                            file={selectedLanguage === 'chinese' ? config.resume.chinese : config.resume.english}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <div className="loading">
                                    <div className="spinner"></div>
                                    <p>Loading resume...</p>
                                </div>
                            }
                            error={
                                <div className="error">
                                    <p>❌ Unable to load resume file</p>
                                    <p>Please ensure the following files have been added:</p>
                                    <ul>
                                        <li>public/files/resume-zh.pdf (Chinese Resume)</li>
                                        <li>public/files/resume-en.pdf (English Resume)</li>
                                    </ul>
                                    <p>You can drag PDF files to the corresponding directory</p>
                                </div>
                            }
                        >
                            <Page
                                pageNumber={pageNumber}
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </Document>
                    </div>
                </div>

                <div className="resume-info">
                    <h3>Resume Information</h3>
                    <div className="info-grid">
                        <div className="info-card">
                            <h4>📄 Chinese Resume</h4>
                            <p>Detailed display of my education background, internship experience, project experience and skill expertise, suitable for domestic company applications.</p>
                        </div>
                        <div className="info-card">
                            <h4>📄 English Resume</h4>
                            <p>Comprehensive overview of my education, internship experience, projects and skills, suitable for international companies.</p>
                        </div>
                        <div className="info-card">
                            <h4>💼 Product Manager Alignment</h4>
                            <p>The resume highlights experience highly relevant to product manager positions, including core skills such as user research, product design, and data analysis.</p>
                        </div>
                        <div className="info-card">
                            <h4>🎯 Core Advantages</h4>
                            <p>Technical background + Product thinking + Internship experience + Project practice, forming a complete product manager capability system.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Resume;
